import { Heart, Users, Calendar, Building, MapPin, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Reveal } from '@/components/ui/Reveal';
import { Button } from '@/components/ui/button';
import { useSEO } from '@/lib/seo';

export default function LeGroupe() {
  useSEO({
    title: "Le Groupe Ambulances Beaulon | Notre histoire et nos valeurs",
    description: "Découvrez l'histoire du Groupe Ambulances Beaulon : Ambulance Beaulon et Ambulance 03 Avermes Saint-Ennemond. Une histoire d'engagement territorial dans l'Allier.",
    keywords: "ambulance beaulon histoire, groupe sanitaire allier, ambulance avermes saint ennemond"
  });

  return (
    <div>
      {/* Hero */}
      <section className="hero-wash">
        <div className="container-app pt-14 pb-12 lg:pt-20 lg:pb-16 grid lg:grid-cols-2 gap-10 items-center">
          <Reveal>
            <div>
              <div className="inline-flex items-center gap-2 mb-5 rounded-full bg-white border border-brand-100 px-3 py-1.5 shadow-sm">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500"></span>
                <span className="text-xs font-medium text-brand-800 uppercase tracking-[0.14em]">Notre histoire</span>
              </div>
              <div className="mb-5">
                <div className="mb-3" data-testid="legroupe-brand-title">
                  <h1 className="font-heading tracking-tight leading-[1.05]">
                    <span className="block sm:inline text-anthracite-900 font-semibold text-3xl sm:text-4xl lg:text-5xl">
                      Groupe Ambulances
                    </span>{' '}
                    <span className="block sm:inline text-brand-800 font-extrabold text-4xl sm:text-5xl lg:text-6xl">
                      Beaulon
                    </span>
                  </h1>
                  <div className="mt-4 h-1 w-16 rounded-full bg-brand-500"></div>
                </div>
                <div className="mt-3 h-1 w-20 rounded-full bg-brand-500"></div>
              </div>
              <h1 className="h2 text-anthracite-950">Un groupe sanitaire ancré dans l'Allier</h1>
              <p className="lead mt-5 max-w-xl">
                Né d'un engagement local fort, le <strong className="text-anthracite-900 font-semibold">Groupe Ambulances Beaulon</strong> est aujourd'hui une référence du transport sanitaire dans l'Allier. Une histoire d'équipes, de proximité et de modernisation continue.
              </p>
            </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="relative aspect-[4/3] rounded-3xl overflow-hidden border bg-white shadow-card">
              <img src="https://images.pexels.com/photos/6519887/pexels-photo-6519887.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200" alt="Équipe Groupe Ambulances Beaulon" className="w-full h-full object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-pad">
        <div className="container-app max-w-4xl">
          <Reveal>
            <h2 className="h2 text-anthracite-950">Une histoire d'engagement local</h2>
          </Reveal>
          <div className="mt-10 space-y-8">
            {[
              { icon: Heart, title: 'Les origines — Ambulance Beaulon', text: "Tout commence par un engagement simple : être là, dans nos villages de l'Allier, lorsque les habitants ont besoin d’un transport sanitaire de confiance. Au fil des années, Ambulance Beaulon devient un acteur reconnu pour son sérieux et sa proximité." },
              { icon: Building, title: 'Un développement progressif', text: "L’entreprise grandit, fidèle à ses valeurs : réactivité, professionnalisme et accompagnement humain. De nouvelles implantations naissent autour de Garnat-sur-Engièvre, Diou puis Tronget, pour mailler le territoire et réduire les distances d’intervention." },
              { icon: MapPin, title: "Une implantation élargie", text: "Au-delà de ses bastions historiques, le groupe se déploie vers le Bourbonnais et investit dans des véhicules récents, du matériel adapté et la formation continue des équipes." },
              { icon: Users, title: 'La création d’Ambulance 03 Avermes Saint-Ennemond', text: "Pour mieux couvrir la région de Moulins, le groupe crée Ambulance 03 Avermes Saint-Ennemond. Une même culture, une même exigence : réactivité, humanité, professionnalisme." },
              { icon: Calendar, title: "Modernisation et proximité", text: "Aujourd'hui, le groupe poursuit sa modernisation : véhicules récents, matériel spécialisé (bariatrique jusqu’à 500 kg, lit hospitalier), plannings optimisés, et une attention de chaque instant portée aux patients et aux familles." },
            ].map((step, i) => (
              <Reveal key={step.title} delay={i * 80}>
                <div className="relative pl-14">
                  <div className="absolute left-0 top-0 h-11 w-11 rounded-xl bg-brand-50 border border-brand-100 flex items-center justify-center text-brand-700">
                    <step.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-anthracite-950">{step.title}</h3>
                  <p className="mt-2 text-anthracite-700 leading-relaxed">{step.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Valeurs */}
      <section className="section-pad bg-anthracite-100/60">
        <div className="container-app">
          <Reveal>
            <h2 className="h2 text-anthracite-950 text-center">Nos valeurs</h2>
          </Reveal>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              { title: 'Proximité', text: "Une couverture territoriale fine pour être près de vous, partout dans l'Allier." },
              { title: 'Professionnalisme', text: "Des équipes formées, des procédures rigoureuses, du matériel adapté à chaque transport." },
              { title: 'Humanité', text: "Écoute, respect et discrétion sont les fondements de notre métier." },
            ].map((v, i) => (
              <Reveal key={v.title} delay={i * 60}>
                <div className="card-base p-6">
                  <h3 className="h3 text-anthracite-950">{v.title}</h3>
                  <p className="mt-2 text-anthracite-700">{v.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-10 text-center">
            <Link to="/implantations">
              <Button>Voir nos implantations <ArrowRight className="h-4 w-4" /></Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
